import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Typography,
  IconButton,
  Popper,
  ClickAwayListener,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
// Mock service removed - search suggestions disabled temporarily

export interface SearchQuery {
  text: string;
  filters: {
    funnelStages: string[];
    personas: string[];
    tags: string[];
  };
}

interface UniversalSearchBarProps {
  onSearch: (query: SearchQuery) => void;
  placeholder?: string;
  value?: SearchQuery;
  showFilters?: boolean;
}

const UniversalSearchBar: React.FC<UniversalSearchBarProps> = ({
  onSearch,
  placeholder = "Search content...",
  value,
  showFilters = true,
}) => {
  const [inputValue, setInputValue] = useState(value?.text || '');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeFilters, setActiveFilters] = useState(value?.filters || {
    funnelStages: [],
    personas: [],
    tags: [],
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get search suggestions
  const getSuggestions = async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    // Search suggestions temporarily disabled after removing mock service
    // TODO: Implement real search suggestions from API
    setSuggestions([]);
  };

  // Cleanup effect to clear timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Debounced search function
  const debouncedSearch = (searchValue: string) => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for 1 second delay
    searchTimeoutRef.current = setTimeout(() => {
      const query: SearchQuery = {
        text: searchValue.trim(),
        filters: activeFilters,
      };

      onSearch(query);
      setShowSuggestions(false);
    }, 1000); // 1 second delay
  };

  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);

    // Get suggestions for regular text
    getSuggestions(newValue);
    setShowSuggestions(newValue.length >= 2);

    // Trigger debounced search for live search functionality
    // Only search if we have some meaningful input (at least 2 characters)
    if (newValue.length >= 2) {
      debouncedSearch(newValue);
    } else if (newValue.length === 0) {
      // Immediately search when input is cleared
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      onSearch({
        text: '',
        filters: {
          funnelStages: [],
          personas: [],
          tags: [],
        },
      });
    }
  };

  // Handle immediate search submission (when Enter is pressed)
  const handleSearch = () => {
    // Clear any pending debounced search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const query: SearchQuery = {
      text: inputValue.trim(),
      filters: activeFilters,
    };

    onSearch(query);
    setShowSuggestions(false);
  };

  // Handle key press
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    } else if (event.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    // Clear any pending debounced search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setInputValue(suggestion);
    setShowSuggestions(false);
    // Trigger search with the selected suggestion immediately
    setTimeout(() => {
      onSearch({
        text: suggestion.trim(),
        filters: activeFilters,
      });
    }, 0);
  };

  // Clear search
  const handleClear = () => {
    // Clear any pending debounced search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setInputValue('');
    setActiveFilters({
      funnelStages: [],
      personas: [],
      tags: [],
    });
    setSuggestions([]);
    setShowSuggestions(false);
    onSearch({
      text: '',
      filters: {
        funnelStages: [],
        personas: [],
        tags: [],
      },
    });
  };

  // Remove individual filter chip
  const removeFilter = (type: keyof typeof activeFilters, value: string) => {
    // Clear any pending debounced search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const newFilters = {
      ...activeFilters,
      [type]: activeFilters[type].filter(item => item !== value),
    };
    setActiveFilters(newFilters);

    // Trigger immediate search with updated filters
    onSearch({
      text: inputValue.trim(),
      filters: newFilters,
    });
  };

  // Count total active filters
  const totalFilters = Object.values(activeFilters).reduce((sum, arr) => sum + arr.length, 0);

  const open = showSuggestions;

  return (
    <ClickAwayListener onClickAway={() => {
      setShowSuggestions(false);
    }}>
      <Box sx={{ position: 'relative', width: '100%', maxWidth: 600 }}>
        <TextField
          ref={inputRef}
          fullWidth
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          placeholder={placeholder}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {totalFilters > 0 && (
                  <Chip
                    size="small"
                    label={`${totalFilters} filter${totalFilters > 1 ? 's' : ''}`}
                    color="primary"
                    variant="outlined"
                    icon={<FilterIcon />}
                    sx={{ mr: 0.75 }}
                  />
                )}
                {inputValue && (
                  <IconButton size="small" onClick={handleClear} sx={{ p: 0.25 }}>
                    <ClearIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          sx={(theme) => ({
            '& .MuiOutlinedInput-root': {
              backgroundColor: theme.palette.background.paper,
              borderRadius: '8px',
              fontSize: '12.5px',
              minHeight: 34,
              '& fieldset': {
                borderColor: theme.palette.divider,
              },
              '&:hover fieldset': {
                borderColor: theme.palette.mode === 'light'
                  ? theme.palette.brand.teal
                  : theme.palette.brand.cyan,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.mode === 'light'
                  ? theme.palette.brand.teal
                  : theme.palette.brand.cyan,
              },
            },
            '& .MuiInputBase-input': {
              fontSize: '12.5px',
              py: '7px',
              '&::placeholder': {
                fontSize: '12.5px',
                opacity: 0.6,
              },
            },
          })}
        />

        {/* Active Filter Chips */}
        {totalFilters > 0 && showFilters && (
          <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {activeFilters.funnelStages.map(stage => (
              <Chip
                key={`stage-${stage}`}
                label={`Stage: ${stage}`}
                size="small"
                onDelete={() => removeFilter('funnelStages', stage)}
                variant="outlined"
                sx={(theme) => ({
                  borderColor: theme.palette.mode === 'light'
                    ? theme.palette.brand.teal
                    : theme.palette.brand.cyan,
                  color: theme.palette.mode === 'light'
                    ? theme.palette.brand.teal
                    : theme.palette.brand.cyan,
                })}
              />
            ))}
            {activeFilters.personas.map(persona => (
              <Chip
                key={`persona-${persona}`}
                label={`Persona: ${persona}`}
                size="small"
                onDelete={() => removeFilter('personas', persona)}
                variant="outlined"
                sx={(theme) => ({
                  borderColor: theme.palette.mode === 'light'
                    ? theme.palette.brand.teal
                    : theme.palette.brand.cyan,
                  color: theme.palette.mode === 'light'
                    ? theme.palette.brand.teal
                    : theme.palette.brand.cyan,
                })}
              />
            ))}
            {activeFilters.tags.map(tag => (
              <Chip
                key={`tag-${tag}`}
                label={`Tag: ${tag}`}
                size="small"
                onDelete={() => removeFilter('tags', tag)}
                variant="outlined"
                sx={(theme) => ({
                  borderColor: theme.palette.mode === 'light'
                    ? theme.palette.brand.teal
                    : theme.palette.brand.cyan,
                  color: theme.palette.mode === 'light'
                    ? theme.palette.brand.teal
                    : theme.palette.brand.cyan,
                })}
              />
            ))}
          </Box>
        )}

        {/* Suggestions Popper */}
        <Popper
          open={open}
          anchorEl={inputRef.current}
          placement="bottom-start"
          style={{ width: inputRef.current?.offsetWidth, zIndex: 1300 }}
          ref={popperRef}
        >
          <Paper
            elevation={4}
            sx={(theme) => ({
              mt: 0.75,
              maxHeight: 280,
              overflow: 'auto',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '8px',
            })}
          >
            {showSuggestions && suggestions.length > 0 && (
              <>
                <Box sx={(theme) => ({
                  px: 1.5,
                  py: 0.75,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                })}>
                  <Typography sx={{
                    fontSize: '10.5px',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}>
                    Suggestions
                  </Typography>
                </Box>
                <List dense disablePadding>
                  {suggestions.map((suggestion, index) => (
                    <ListItem
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{ cursor: 'pointer', py: 0.5, px: 1.5 }}
                    >
                      <ListItemText
                        primary={suggestion}
                        primaryTypographyProps={{ fontSize: '12.5px' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {showSuggestions && suggestions.length === 0 && (
              <Box sx={{ px: 1.5, py: 1 }}>
                <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                  No suggestions found
                </Typography>
              </Box>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default UniversalSearchBar;